import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { useSmartBack } from "@/lib/useSmartBack";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
  ArrowLeft,
  Trash2,
  Package,
  FileText,
  Target,
  Lightbulb,
  Sparkles,
  BookOpen,
  Save,
  RefreshCw,
  AlertTriangle,
  Plus,
  X,
  Heart,
  MessageCircle,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import {
  knowledgeApi,
  type KnowledgeBase,
  type KnowledgeBaseType,
  type KnowledgeBaseContentMeta,
  type CompetitorAd,
  type CompetitorMeta,
  type ProductMeta,
  type ProductPersona,
  type ProductResultado,
  type ReferenceMeta,
  type ReferenceType,
} from "@/api/knowledge";
import { queryKeys } from "@/lib/queryKeys";
import { LoadingPlanet } from "@/components/LoadingPlanet";
import { MarkdownLite, splitMdSections } from "@/components/MarkdownLite";
import { ColorChipsInput, BrandFontUploader } from "@/components/ui";
import {
  productLibraryApi,
  type ProductBrandIdentity,
  type PeoplePhoto,
  type CreativeRef,
  type LogoRef,
} from "@/api/product-library";
import { api } from "@/lib/api";

// Paleta MIV: navy pra onboarding (foundational), orange pra os outros
const TYPE_META: Record<
  KnowledgeBaseType,
  { label: string; icon: typeof BookOpen; color: string }
> = {
  onboarding: { label: "Onboarding", icon: BookOpen, color: "var(--nd-blue)" },
  product: { label: "Produto", icon: Package, color: "var(--nd-orange)" },
  material: { label: "Material", icon: FileText, color: "var(--nd-orange)" },
  competitor: { label: "Concorrente", icon: Target, color: "var(--nd-orange)" },
  reference: { label: "Referência", icon: Lightbulb, color: "var(--nd-orange)" },
  custom: { label: "Custom", icon: Sparkles, color: "var(--nd-orange)" },
};

const inputStyle: React.CSSProperties = {
  background: "var(--wf-paper)",
  border: "1px solid var(--wf-ink-5)",
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 14,
  fontFamily: "var(--wf-font-body)",
  color: "var(--wf-ink)",
  width: "100%",
  outline: "none",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 100,
  resize: "vertical" as const,
  lineHeight: 1.55,
};

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>{label}</span>
      {hint && (
        <span style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: -2 }}>{hint}</span>
      )}
      {children}
    </label>
  );
}

function ChipsInput({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const add = (raw: string) => {
    const v = raw.trim();
    if (!v || value.includes(v)) return;
    onChange([...value, v]);
    setDraft("");
  };
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        padding: 8,
        minHeight: 44,
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 8,
        background: "var(--wf-paper)",
      }}
    >
      {value.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className="wf-pill wf-pill--orange"
          style={{ fontSize: 12, padding: "2px 8px", cursor: "pointer" }}
          onClick={() => remove(i)}
        >
          {v} ×
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add(draft);
          } else if (e.key === "Backspace" && !draft && value.length > 0) {
            remove(value.length - 1);
          }
        }}
        placeholder={value.length === 0 ? placeholder : ""}
        style={{
          flex: 1,
          minWidth: 120,
          border: "none",
          outline: "none",
          background: "transparent",
          fontSize: 13,
          fontFamily: "var(--wf-font-body)",
        }}
      />
    </div>
  );
}

interface FormState {
  name: string;
  description: string;
  contentText: string;
  product: ProductMeta;
  brandIdentity: ProductBrandIdentity;
  peopleLibrary: PeoplePhoto[];
  creativeLibrary: CreativeRef[];
  logos: LogoRef[];
  reference: ReferenceMeta;
}

function fromKb(kb: KnowledgeBase): FormState {
  const meta = (kb.contentMeta ?? {}) as Record<string, unknown> & {
    product?: ProductMeta;
    brandIdentity?: ProductBrandIdentity;
    peopleLibrary?: PeoplePhoto[];
    creativeLibrary?: CreativeRef[];
    logos?: LogoRef[];
    reference?: ReferenceMeta;
  };
  return {
    name: kb.name,
    description: kb.description ?? "",
    contentText: kb.contentText,
    product: (meta.product ?? {}) as ProductMeta,
    brandIdentity: (meta.brandIdentity ?? {}) as ProductBrandIdentity,
    peopleLibrary: meta.peopleLibrary ?? [],
    creativeLibrary: meta.creativeLibrary ?? [],
    logos: meta.logos ?? [],
    reference: (meta.reference ?? { referenceType: "custom" }) as ReferenceMeta,
  };
}

function isDirty(a: FormState, b: FormState): boolean {
  if (a.name !== b.name) return true;
  if (a.description !== b.description) return true;
  if (a.contentText !== b.contentText) return true;
  if (JSON.stringify(a.reference ?? {}) !== JSON.stringify(b.reference ?? {})) return true;
  if (JSON.stringify(a.product ?? {}) !== JSON.stringify(b.product ?? {})) return true;
  if (
    JSON.stringify(a.brandIdentity ?? {}) !==
    JSON.stringify(b.brandIdentity ?? {})
  )
    return true;
  if (
    JSON.stringify(a.peopleLibrary ?? []) !==
    JSON.stringify(b.peopleLibrary ?? [])
  )
    return true;
  if (
    JSON.stringify(a.creativeLibrary ?? []) !==
    JSON.stringify(b.creativeLibrary ?? [])
  )
    return true;
  if (JSON.stringify(a.logos ?? []) !== JSON.stringify(b.logos ?? []))
    return true;
  return false;
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - ts) / 60000);
  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `há ${diffD}d`;
  return new Intl.DateTimeFormat("pt-BR", { day: "numeric", month: "short" }).format(new Date(iso));
}

// ─────────────────────────────────────────────────────────────
// Tabs
// ─────────────────────────────────────────────────────────────

type ProductTab =
  | "identidade"
  | "oferta"
  | "beneficios"
  | "publico"
  | "mercado"
  | "visual";
type SimpleTab = "conteudo";
type CompetitorTabKey = "dossie" | "perfil" | "criativos" | "site";
type AnyTab = ProductTab | SimpleTab | CompetitorTabKey;

const PRODUCT_TABS: { key: ProductTab; label: string }[] = [
  { key: "identidade", label: "Identidade" },
  { key: "oferta", label: "Oferta" },
  { key: "beneficios", label: "Benefícios" },
  { key: "publico", label: "Público" },
  { key: "mercado", label: "Mercado" },
  { key: "visual", label: "Identidade visual" },
];

const SIMPLE_TABS: { key: SimpleTab; label: string }[] = [
  { key: "conteudo", label: "Conteúdo" },
];

function TabsBar<T extends string>({
  tabs,
  current,
  onChange,
}: {
  tabs: { key: T; label: string }[];
  current: T;
  onChange: (next: T) => void;
}) {
  return (
    <div className="wf-tabs">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={`wf-tab ${current === t.key ? "wf-tab--active" : ""}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Product tabs
// ─────────────────────────────────────────────────────────────

function PersonaCard({
  persona,
  onChange,
  onRemove,
  index,
}: {
  persona: ProductPersona;
  onChange: (next: ProductPersona) => void;
  onRemove: () => void;
  index: number;
}) {
  return (
    <div
      style={{
        background: "var(--wf-paper)",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 10,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--wf-ink-3)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Persona {index + 1}
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onRemove}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 4,
            color: "var(--wf-ink-3)",
            display: "flex",
            alignItems: "center",
          }}
          aria-label="Remover persona"
          title="Remover persona"
        >
          <X size={14} strokeWidth={1.75} />
        </button>
      </div>
      <Field label="Nome fictício">
        <input
          style={inputStyle}
          value={persona.nomeFicticio}
          onChange={(e) => onChange({ ...persona, nomeFicticio: e.target.value })}
          placeholder="Ex: Camila, 32, dentista de clínica"
        />
      </Field>
      <Field label="Perfil demográfico" hint="Idade, profissão, contexto">
        <input
          style={inputStyle}
          value={persona.perfilDemografico ?? ""}
          onChange={(e) => onChange({ ...persona, perfilDemografico: e.target.value })}
          placeholder="32 anos · dentista · clínica própria"
        />
      </Field>
      <Field label="Dores">
        <ChipsInput
          value={persona.dores ?? []}
          onChange={(v) => onChange({ ...persona, dores: v })}
          placeholder="Adicione e pressione Enter"
        />
      </Field>
      <Field label="Desejos">
        <ChipsInput
          value={persona.desejos ?? []}
          onChange={(v) => onChange({ ...persona, desejos: v })}
        />
      </Field>
      <Field label="Objeções">
        <ChipsInput
          value={persona.objecoes ?? []}
          onChange={(v) => onChange({ ...persona, objecoes: v })}
          placeholder="O 'mas' que essa persona levanta"
        />
      </Field>
    </div>
  );
}

function ResultadoRow({
  resultado,
  onChange,
  onRemove,
}: {
  resultado: ProductResultado;
  onChange: (next: ProductResultado) => void;
  onRemove: () => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "120px 1fr 160px 28px",
        gap: 8,
        alignItems: "center",
      }}
    >
      <select
        value={resultado.tipo}
        onChange={(e) =>
          onChange({ ...resultado, tipo: e.target.value as "proprio" | "aluno" })
        }
        style={inputStyle}
      >
        <option value="proprio">Próprio</option>
        <option value="aluno">Aluno</option>
      </select>
      <input
        value={resultado.descricao}
        onChange={(e) => onChange({ ...resultado, descricao: e.target.value })}
        placeholder="Descrição do resultado"
        style={inputStyle}
      />
      <input
        value={resultado.metrica ?? ""}
        onChange={(e) => onChange({ ...resultado, metrica: e.target.value })}
        placeholder="Métrica (ex: +342%)"
        style={inputStyle}
      />
      <button
        type="button"
        onClick={onRemove}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 4,
          color: "var(--wf-ink-3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title="Remover"
      >
        <X size={14} strokeWidth={1.75} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Identidade visual (brand library — paleta, fontes, logo, imagens
// da marca/produto, criativos passados). Save junto com o form do KB.
// ─────────────────────────────────────────────────────────────

const MAX_PEOPLE = 15;
const MAX_CREATIVES = 30;
const MAX_LOGOS = 6;

const CREATIVE_CATEGORIES = [
  "post_educacional",
  "post_oferta",
  "post_evento",
  "story",
  "carrossel_capa",
  "carrossel_slide",
  "outro",
] as const;


/**
 * Tab "Identidade visual" do KB de produto. Controlled — recebe state +
 * setters do form geral do KbDetail. Save acontece pelo botão "Salvar"
 * único do KB (não tem save próprio aqui), e isDirty do form captura
 * mudanças automaticamente.
 *
 * Carrega a library do server pra enriquecer entries com URL renderizável
 * (contentPath) — assets armazenados só têm attachmentId no contentMeta.
 */
function ProductVisualTab({
  companyId,
  productKbId,
  brand,
  people,
  creatives,
  logos,
  onBrandChange,
  onPeopleChange,
  onCreativesChange,
  onLogosChange,
}: {
  companyId: string;
  productKbId: string;
  brand: ProductBrandIdentity;
  people: PeoplePhoto[];
  creatives: CreativeRef[];
  logos: LogoRef[];
  onBrandChange: (next: ProductBrandIdentity) => void;
  onPeopleChange: (next: PeoplePhoto[]) => void;
  onCreativesChange: (next: CreativeRef[]) => void;
  onLogosChange: (next: LogoRef[]) => void;
}) {
  // Lookup das URLs dos assets via endpoint enriched. Server resolve
  // contentPath pra cada attachmentId — UI só guarda o id no form, mas
  // precisa da URL pra <img>.
  const libraryQuery = useQuery({
    queryKey: ["product-library", companyId, productKbId],
    queryFn: () => productLibraryApi.get(companyId, productKbId),
  });

  const peopleUrlMap = useMemo(() => {
    const map = new Map<string, string | null>();
    libraryQuery.data?.peopleLibrary.forEach((p) =>
      map.set(p.attachmentId, p.contentPath),
    );
    return map;
  }, [libraryQuery.data]);
  const creativeUrlMap = useMemo(() => {
    const map = new Map<string, string | null>();
    libraryQuery.data?.creativeLibrary.forEach((c) =>
      map.set(c.attachmentId, c.contentPath),
    );
    return map;
  }, [libraryQuery.data]);
  const logoUrlMap = useMemo(() => {
    const map = new Map<string, string | null>();
    libraryQuery.data?.logos?.forEach((l) =>
      map.set(l.attachmentId, l.contentPath),
    );
    return map;
  }, [libraryQuery.data]);

  const peopleEnriched: PeoplePhoto[] = people.map((p) => ({
    ...p,
    contentPath: p.contentPath ?? peopleUrlMap.get(p.attachmentId) ?? null,
  }));
  const creativesEnriched: CreativeRef[] = creatives.map((c) => ({
    ...c,
    contentPath: c.contentPath ?? creativeUrlMap.get(c.attachmentId) ?? null,
  }));
  const logosEnriched: LogoRef[] = logos.map((l) => ({
    ...l,
    contentPath: l.contentPath ?? logoUrlMap.get(l.attachmentId) ?? null,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <BrandIdentityForm companyId={companyId} brand={brand} onChange={onBrandChange} />

      <LogoLibraryGrid
        companyId={companyId}
        logos={logosEnriched}
        onChange={onLogosChange}
      />

      <PeopleLibraryGrid
        companyId={companyId}
        people={peopleEnriched}
        onChange={onPeopleChange}
      />

      <CreativeLibraryGrid
        companyId={companyId}
        creatives={creativesEnriched}
        onChange={onCreativesChange}
      />
    </div>
  );
}

function BrandIdentityForm({
  companyId,
  brand,
  onChange,
}: {
  companyId: string;
  brand: ProductBrandIdentity;
  onChange: (next: ProductBrandIdentity) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Field label="Paleta" hint="Hex codes (#RRGGBB) ou use o seletor. 3-5 cores principais.">
        <ColorChipsInput
          value={brand.paleta ?? []}
          onChange={(next) => onChange({ ...brand, paleta: next })}
        />
      </Field>

      <Field label="Fontes" hint="Nomes separados por vírgula. Primeira é principal.">
        <ChipsInput
          value={brand.fontes ?? []}
          onChange={(next) => onChange({ ...brand, fontes: next })}
          placeholder="Helvetica Black"
        />
      </Field>

      <Field
        label="Arquivos de fonte (recomendado)"
        hint="Suba o arquivo .ttf/.otf/.woff da fonte do título e do corpo. Sem isso, a arte usa a fonte padrão do sistema (Bricolage) quando a sua não é uma fonte gratuita do Google."
      >
        <BrandFontUploader
          companyId={companyId}
          value={brand.fontFiles ?? []}
          onChange={(next) => onChange({ ...brand, fontFiles: next.length ? next : undefined })}
        />
      </Field>

      <Field
        label="Estilo de lettering"
        hint="Como aparece o texto burned-in na arte (peso, cor, alinhamento, sombra/box)."
      >
        <textarea
          style={{ ...textareaStyle, minHeight: 70 }}
          value={brand.letteringStyle ?? ""}
          onChange={(e) => onChange({ ...brand, letteringStyle: e.target.value })}
          placeholder="Ex: Caixa alta peso 800, branco com outline preto 4px, alinhamento bottom-left"
        />
      </Field>

      <Field label="Tom visual" hint="Em uma frase: a vibe da identidade.">
        <input
          style={inputStyle}
          value={brand.tomVisual ?? ""}
          onChange={(e) => onChange({ ...brand, tomVisual: e.target.value })}
          placeholder="editorial frio premium / energético colorido / minimalista clean"
        />
      </Field>

      <Field
        label="Diretrizes visuais do produto"
        hint="Regras visuais específicas deste produto — sobrescrevem as diretrizes da marca pra qualquer tarefa que selecione este produto."
      >
        <textarea
          style={{ ...textareaStyle, minHeight: 120 }}
          value={brand.diretrizes ?? ""}
          onChange={(e) => onChange({ ...brand, diretrizes: e.target.value })}
          placeholder="Ex: Sempre fotos em ambiente clínico, branco e azul predominantes. Logo no canto inferior direito. Nunca usar lifestyle de praia/aventura."
        />
      </Field>
    </div>
  );
}

function PeopleLibraryGrid({
  companyId,
  people,
  onChange,
}: {
  companyId: string;
  people: PeoplePhoto[];
  onChange: (next: PeoplePhoto[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next = [...people];
    for (const file of Array.from(files)) {
      if (next.length >= MAX_PEOPLE) break;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const result = await api.postForm<{ assetId: string }>(
          `/companies/${companyId}/assets/images`,
          fd,
        );
        next.push({
          attachmentId: result.assetId,
          contentPath: `/api/assets/${result.assetId}/content`,
          byteSize: null,
          contentType: "image/jpeg",
        });
      } catch (err) {
        alert(`Falha no upload: ${(err as Error).message}`);
      }
    }
    onChange(next);
  };

  return (
    <Field
      label="Imagens da marca / produto"
      hint={`${people.length} de ${MAX_PEOPLE}. O que deve aparecer e ser preservado nas artes: a pessoa (se for marca pessoal), o produto físico, embalagem, loja, ambiente, mascote. Descreva o que é cada imagem.`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleUpload(e.target.files)}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 10,
        }}
      >
        {people.map((p, i) => (
          <AssetCard
            key={p.attachmentId}
            contentPath={p.contentPath}
            onRemove={() => onChange(people.filter((_, j) => j !== i))}
          >
            <input
              style={{ ...inputStyle, fontSize: 12, padding: "6px 8px" }}
              value={p.expertName ?? ""}
              onChange={(e) => {
                const next = [...people];
                next[i] = { ...p, expertName: e.target.value };
                onChange(next);
              }}
              placeholder="O que é (ex: Maria Silva / embalagem / loja)"
            />
            <input
              style={{
                ...inputStyle,
                fontSize: 12,
                padding: "6px 8px",
                marginTop: 4,
              }}
              value={p.context ?? ""}
              onChange={(e) => {
                const next = [...people];
                next[i] = { ...p, context: e.target.value };
                onChange(next);
              }}
              placeholder="Contexto (ex: foto de palco / fundo branco / em uso)"
            />
          </AssetCard>
        ))}
        {people.length < MAX_PEOPLE && (
          <UploadCard onClick={() => inputRef.current?.click()} />
        )}
      </div>
    </Field>
  );
}

function CreativeLibraryGrid({
  companyId,
  creatives,
  onChange,
}: {
  companyId: string;
  creatives: CreativeRef[];
  onChange: (next: CreativeRef[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next = [...creatives];
    for (const file of Array.from(files)) {
      if (next.length >= MAX_CREATIVES) break;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const result = await api.postForm<{ assetId: string }>(
          `/companies/${companyId}/assets/images`,
          fd,
        );
        next.push({
          attachmentId: result.assetId,
          contentPath: `/api/assets/${result.assetId}/content`,
          byteSize: null,
          contentType: "image/jpeg",
          category: "outro",
        });
      } catch (err) {
        alert(`Falha no upload: ${(err as Error).message}`);
      }
    }
    onChange(next);
  };

  return (
    <Field
      label="Criativos de referência"
      hint={`${creatives.length} de ${MAX_CREATIVES}. Posts/carrosséis passados que viraram referência de estilo.`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleUpload(e.target.files)}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 10,
        }}
      >
        {creatives.map((c, i) => (
          <AssetCard
            key={c.attachmentId}
            contentPath={c.contentPath}
            onRemove={() => onChange(creatives.filter((_, j) => j !== i))}
          >
            <select
              style={{ ...inputStyle, fontSize: 12, padding: "6px 8px" }}
              value={c.category}
              onChange={(e) => {
                const next = [...creatives];
                next[i] = { ...c, category: e.target.value };
                onChange(next);
              }}
            >
              {CREATIVE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.replace(/_/g, " ")}
                </option>
              ))}
            </select>
            <input
              style={{
                ...inputStyle,
                fontSize: 12,
                padding: "6px 8px",
                marginTop: 4,
              }}
              value={c.notes ?? ""}
              onChange={(e) => {
                const next = [...creatives];
                next[i] = { ...c, notes: e.target.value };
                onChange(next);
              }}
              placeholder="anotação (opcional)"
            />
          </AssetCard>
        ))}
        {creatives.length < MAX_CREATIVES && (
          <UploadCard onClick={() => inputRef.current?.click()} />
        )}
      </div>
    </Field>
  );
}

function LogoLibraryGrid({
  companyId,
  logos,
  onChange,
}: {
  companyId: string;
  logos: LogoRef[];
  onChange: (next: LogoRef[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next = [...logos];
    for (const file of Array.from(files)) {
      if (next.length >= MAX_LOGOS) break;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const result = await api.postForm<{ assetId: string }>(
          `/companies/${companyId}/assets/images`,
          fd,
        );
        next.push({
          attachmentId: result.assetId,
          contentPath: `/api/assets/${result.assetId}/content`,
          byteSize: null,
          contentType: "image/png",
          variant: "principal",
        });
      } catch (err) {
        alert(`Falha no upload: ${(err as Error).message}`);
      }
    }
    onChange(next);
  };

  return (
    <Field
      label="Logo do produto"
      hint={`${logos.length} de ${MAX_LOGOS}. PNG com fundo transparente de preferência. Entra em todas as artes (e o agente não inventa logo se não tiver).`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleUpload(e.target.files)}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 10,
        }}
      >
        {logos.map((l, i) => (
          <AssetCard
            key={l.attachmentId}
            contentPath={l.contentPath}
            onRemove={() => onChange(logos.filter((_, j) => j !== i))}
          >
            <input
              style={{ ...inputStyle, fontSize: 12, padding: "6px 8px" }}
              value={l.variant ?? ""}
              onChange={(e) => {
                const next = [...logos];
                next[i] = { ...l, variant: e.target.value };
                onChange(next);
              }}
              placeholder="variante (principal, branco, ícone…)"
            />
            <input
              style={{
                ...inputStyle,
                fontSize: 12,
                padding: "6px 8px",
                marginTop: 4,
              }}
              value={l.notes ?? ""}
              onChange={(e) => {
                const next = [...logos];
                next[i] = { ...l, notes: e.target.value };
                onChange(next);
              }}
              placeholder="anotação (opcional)"
            />
          </AssetCard>
        ))}
        {logos.length < MAX_LOGOS && (
          <UploadCard onClick={() => inputRef.current?.click()} />
        )}
      </div>
    </Field>
  );
}

function AssetCard({
  contentPath,
  onRemove,
  children,
}: {
  contentPath: string | null;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: "var(--wf-paper)",
        border: "1px solid var(--wf-ink-5)",
        borderRadius: 10,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {contentPath ? (
        <img
          src={contentPath}
          alt=""
          style={{
            width: "100%",
            aspectRatio: "1 / 1",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            aspectRatio: "1 / 1",
            background: "var(--wf-ink-5)",
          }}
        />
      )}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          if (confirm("Remover essa imagem da biblioteca?")) onRemove();
        }}
        aria-label="Remover da biblioteca"
        title="Remover da biblioteca"
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          width: 30,
          height: 30,
          borderRadius: 8,
          background: "rgba(13,21,38,0.85)",
          color: "white",
          border: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 120ms",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#cc3344";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(13,21,38,0.85)";
        }}
      >
        <Trash2 size={14} strokeWidth={2} />
      </button>
      <div style={{ padding: 8 }}>{children}</div>
    </div>
  );
}

function UploadCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "var(--wf-paper)",
        border: "2px dashed var(--wf-ink-5)",
        borderRadius: 10,
        aspectRatio: "1 / 1",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 4,
        color: "var(--wf-ink-3)",
        fontSize: 12,
        fontFamily: "var(--wf-font-body)",
      }}
    >
      <span style={{ fontSize: 22 }}>+</span>
      Adicionar
    </button>
  );
}

function ProductIdentidadeTab({
  product,
  setProduct,
  name,
  setName,
  description,
  setDescription,
}: {
  product: ProductMeta;
  setProduct: (next: ProductMeta) => void;
  name: string;
  setName: (n: string) => void;
  description: string;
  setDescription: (d: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Field label="Nome da base" hint="Como ela aparece na lista">
        <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Descrição curta" hint="Aparece no card. Opcional">
        <input
          style={inputStyle}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field label="Tipo da oferta" hint="Mentoria, Curso, Serviço, Produto físico, SaaS, Infoproduto, Livro, Palestra…">
        <input
          style={inputStyle}
          value={product.tipo ?? ""}
          onChange={(e) => setProduct({ ...product, tipo: e.target.value })}
          placeholder="Ex: Curso online"
        />
      </Field>
      <Field label="Promessa" hint="UMA frase que sintetiza a transformação">
        <textarea
          style={{ ...textareaStyle, minHeight: 70 }}
          value={product.promessa ?? ""}
          onChange={(e) => setProduct({ ...product, promessa: e.target.value })}
          placeholder="Ex: Tirar do papel a oferta da sua mentoria em 21 dias"
        />
      </Field>
      <Field
        label="Mecanismo único da solução"
        hint="O que essa oferta faz que NENHUMA outra oferta similar no mercado faz"
      >
        <textarea
          style={textareaStyle}
          value={product.mecanismoUnicoSolucao ?? ""}
          onChange={(e) => setProduct({ ...product, mecanismoUnicoSolucao: e.target.value })}
          placeholder="A 'mágica única' que diferencia"
        />
      </Field>
      <Field label="Descrição completa" hint="1-2 parágrafos que contam o que é a oferta">
        <textarea
          style={{ ...textareaStyle, minHeight: 140 }}
          value={product.descricao ?? ""}
          onChange={(e) => setProduct({ ...product, descricao: e.target.value })}
        />
      </Field>
    </div>
  );
}

function ProductOfertaTab({
  product,
  setProduct,
}: {
  product: ProductMeta;
  setProduct: (next: ProductMeta) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
        <Field label="Preço">
          <input
            style={inputStyle}
            value={product.preco ?? ""}
            onChange={(e) => setProduct({ ...product, preco: e.target.value })}
            placeholder="Ex: R$ 1.997 ou a partir de R$ 297/mês"
          />
        </Field>
        <Field label="Moeda">
          <select
            value={product.moeda ?? "BRL"}
            onChange={(e) => setProduct({ ...product, moeda: e.target.value })}
            style={inputStyle}
          >
            <option value="BRL">BRL</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </Field>
      </div>
      <Field label="Formas de pagamento">
        <ChipsInput
          value={product.formasPagamento ?? []}
          onChange={(v) => setProduct({ ...product, formasPagamento: v })}
          placeholder="Ex: Cartão até 12x, Pix, Boleto"
        />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Formato de entrega" hint="100% online, Híbrido, Presencial em SP">
          <input
            style={inputStyle}
            value={product.formatoEntrega ?? ""}
            onChange={(e) => setProduct({ ...product, formatoEntrega: e.target.value })}
          />
        </Field>
        <Field label="Tempo de acesso">
          <input
            style={inputStyle}
            value={product.tempoAcesso ?? ""}
            onChange={(e) => setProduct({ ...product, tempoAcesso: e.target.value })}
            placeholder="Vitalício / 12 meses"
          />
        </Field>
      </div>
      <Field label="Entregáveis" hint="O que o cliente recebe concretamente">
        <textarea
          style={textareaStyle}
          value={product.entregaveis ?? ""}
          onChange={(e) => setProduct({ ...product, entregaveis: e.target.value })}
          placeholder="Ex: 8 módulos de aulas, 4 mentorias ao vivo, comunidade no Discord, planilhas"
        />
      </Field>
      <Field label="Bônus inclusos">
        <ChipsInput
          value={product.bonus ?? []}
          onChange={(v) => setProduct({ ...product, bonus: v })}
          placeholder="Ex: Workshop ao vivo, Templates, Acesso à comunidade"
        />
      </Field>
      <Field label="Garantia" hint="Política de reembolso, se houver">
        <textarea
          style={{ ...textareaStyle, minHeight: 70 }}
          value={product.garantia ?? ""}
          onChange={(e) => setProduct({ ...product, garantia: e.target.value })}
          placeholder="7 dias de garantia incondicional"
        />
      </Field>
      <Field label="Urgência" hint="Gatilho de urgência/escassez explorado na oferta">
        <textarea
          style={{ ...textareaStyle, minHeight: 70 }}
          value={product.urgencia ?? ""}
          onChange={(e) => setProduct({ ...product, urgencia: e.target.value })}
          placeholder="Ex: Turma fecha sexta, vagas limitadas"
        />
      </Field>
    </div>
  );
}

function ProductBeneficiosTab({
  product,
  setProduct,
}: {
  product: ProductMeta;
  setProduct: (next: ProductMeta) => void;
}) {
  const b = product.beneficios ?? {};
  const update = (patch: Partial<typeof b>) =>
    setProduct({ ...product, beneficios: { ...b, ...patch } });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Field
        label="Benefícios funcionais"
        hint="O que o cliente VAI CONSEGUIR FAZER após comprar"
      >
        <ChipsInput
          value={b.funcionais ?? []}
          onChange={(v) => update({ funcionais: v })}
          placeholder="Ex: Estruturar oferta, gravar VSL, lançar funil"
        />
      </Field>
      <Field label="Benefícios emocionais" hint="Como o cliente vai SE SENTIR">
        <ChipsInput
          value={b.emocionais ?? []}
          onChange={(v) => update({ emocionais: v })}
          placeholder="Ex: Mais confiante, sem culpa, no controle"
        />
      </Field>
      <Field
        label="Benefícios dimensionais"
        hint="Em que dimensão maior da vida vai impactar"
      >
        <ChipsInput
          value={b.dimensionais ?? []}
          onChange={(v) => update({ dimensionais: v })}
          placeholder="Ex: Mais tempo com filhos, liberdade geográfica"
        />
      </Field>
    </div>
  );
}

function ProductPublicoTab({
  product,
  setProduct,
}: {
  product: ProductMeta;
  setProduct: (next: ProductMeta) => void;
}) {
  const personas = product.personas ?? [];
  const updatePersonas = (next: ProductPersona[]) => setProduct({ ...product, personas: next });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Field
        label="Público-alvo (ICP) específico"
        hint="Quem é o cliente ideal desse produto, se diferente do público geral"
      >
        <textarea
          style={textareaStyle}
          value={product.publicoAlvoOverride ?? ""}
          onChange={(e) => setProduct({ ...product, publicoAlvoOverride: e.target.value })}
          placeholder="Ex: Dentistas com clínica própria faturando entre 30-100k/mês"
        />
      </Field>
      <Field
        label="Tom de voz específico (override)"
        hint="Se esse produto fala diferente do tom geral da empresa"
      >
        <ChipsInput
          value={product.tomVozOverride ?? []}
          onChange={(v) => setProduct({ ...product, tomVozOverride: v })}
          placeholder="Profissional, Técnico, Acolhedor…"
        />
      </Field>

      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
            Personas detalhadas
          </span>
          <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>{personas.length}</span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() =>
              updatePersonas([
                ...personas,
                { nomeFicticio: "", dores: [], desejos: [], objecoes: [] },
              ])
            }
            className="wf-btn wf-btn--ghost wf-btn--sm"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <Plus size={11} strokeWidth={2} /> persona
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {personas.map((p, i) => (
            <PersonaCard
              key={i}
              index={i}
              persona={p}
              onChange={(next) => {
                const copy = [...personas];
                copy[i] = next;
                updatePersonas(copy);
              }}
              onRemove={() => updatePersonas(personas.filter((_, idx) => idx !== i))}
            />
          ))}
          {personas.length === 0 && (
            <div
              style={{
                padding: 18,
                textAlign: "center",
                fontSize: 12,
                color: "var(--wf-ink-3)",
                background: "var(--wf-paper)",
                border: "1px dashed var(--wf-ink-4)",
                borderRadius: 8,
              }}
            >
              Nenhuma persona ainda. Adicione pelo menos uma pra os agentes
              entenderem com quem estão falando.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductMercadoTab({
  product,
  setProduct,
}: {
  product: ProductMeta;
  setProduct: (next: ProductMeta) => void;
}) {
  const resultados = product.resultados ?? [];
  const updateResultados = (next: ProductResultado[]) =>
    setProduct({ ...product, resultados: next });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Field
        label="Concorrentes considerados"
        hint="Outras ofertas/produtos que essa pessoa consideraria comprar antes"
      >
        <ChipsInput
          value={product.concorrentes ?? []}
          onChange={(v) => setProduct({ ...product, concorrentes: v })}
          placeholder="Adicione e pressione Enter"
        />
      </Field>

      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
            Resultados / cases
          </span>
          <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>{resultados.length}</span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() =>
              updateResultados([...resultados, { tipo: "proprio", descricao: "" }])
            }
            className="wf-btn wf-btn--ghost wf-btn--sm"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <Plus size={11} strokeWidth={2} /> caso
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {resultados.map((r, i) => (
            <ResultadoRow
              key={i}
              resultado={r}
              onChange={(next) => {
                const copy = [...resultados];
                copy[i] = next;
                updateResultados(copy);
              }}
              onRemove={() => updateResultados(resultados.filter((_, idx) => idx !== i))}
            />
          ))}
        </div>
      </div>

      <Field label="Notas adicionais" hint="Outras nuances que a equipe deve saber">
        <textarea
          style={textareaStyle}
          value={product.notas ?? ""}
          onChange={(e) => setProduct({ ...product, notas: e.target.value })}
        />
      </Field>
    </div>
  );
}

const REFERENCE_TYPE_LABEL: Record<ReferenceType, string> = {
  autoridade: "Autoridade",
  conteudo: "Conteúdo",
  criativo: "Criativo / Visual",
  sazonal: "Sazonal / Campanha",
  landing_page: "Estrutura de página",
  custom: "Custom",
};

/** Renderiza o dossiê de referência em markdown pro contentText (o que o
 *  prompt-builder injeta nos agentes). Só inclui campos preenchidos. */
function renderReferenceContentText(ref: ReferenceMeta): string {
  const L: string[] = [`# Referência · ${REFERENCE_TYPE_LABEL[ref.referenceType ?? "custom"]}`];
  const line = (label: string, v?: string) => {
    if (v && v.trim()) L.push("", `**${label}:**`, v.trim());
  };
  const chips = (label: string, v?: string[]) => {
    if (v && v.length) L.push("", `**${label}:** ${v.join(", ")}`);
  };
  L.push("", "## Análise");
  line("Por que é referência", ref.porQueReferencia);
  line("Posicionamento", ref.posicionamento);
  line("Por que tem autoridade", ref.porQueAutoridade);
  line("Diferencial", ref.diferencial);
  line("Tom de voz", ref.tom);
  chips("Pilares de conteúdo", ref.pilares);
  chips("Sinais de credibilidade", ref.sinais);
  chips("Gatilhos e persuasão", ref.gatilhos);
  line("Estrutura narrativa", ref.estruturaNarrativa);
  chips("Hooks", ref.hooks);
  chips("Bordões / vocabulário", ref.bordoes);
  chips("Formatos que funcionam", ref.formatos);
  line("Estilo visual", ref.estiloVisual);
  chips("Paleta", ref.paleta);
  line("Layout", ref.layout);
  chips("Elementos recorrentes", ref.elementosRecorrentes);
  line("Contexto da campanha", ref.contexto);
  chips("O que faz funcionar", ref.oQueFunciona);
  line("O QUE APLICAR nas suas tarefas", ref.oQueAplicar);
  if (ref.images?.length) {
    L.push(
      "",
      `**Posts/criativos anexados:** ${ref.images.length} (gabarito visual de estilo/composição).`,
    );
    const caps = ref.images.map((im) => im.caption).filter((c): c is string => !!c && c.trim().length > 0);
    if (caps.length) L.push(...caps.map((c) => `- ${c}`));
  }
  if (ref.transcricoes?.length) {
    L.push("", "---", "### Material de apoio — transcrição dos Reels", "_(matéria-prima pra storytelling; o ouro é a Análise acima. Extraia o jeito, nunca copie.)_");
    ref.transcricoes.forEach((t) => L.push("", t));
  }
  return L.join("\n");
}

/** Cabeçalho de seção do editor de referência (Análise / Material de apoio). */
function ReferenceSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "var(--wf-ink-3)",
        marginTop: 6,
        paddingBottom: 6,
        borderBottom: "1px solid var(--wf-ink-5)",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Editor do dossiê de referência. Campos comuns + específicos por tipo, todos
 * editáveis. Estágio 1 = preenchimento manual; estágios seguintes a IA
 * pré-preenche a partir de @ IG / URL / PDF / imagens.
 */
/** Biblioteca visual da referência — posts/carrosséis (raspados ou upload).
 *  Reusa o endpoint /assets/images. Card "Adicionar" no fim. */
function ReferenceImagesGrid({
  companyId,
  images,
  onChange,
}: {
  companyId: string;
  images: NonNullable<ReferenceMeta["images"]>;
  onChange: (next: NonNullable<ReferenceMeta["images"]>) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const next = [...images];
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const result = await api.postForm<{ assetId: string }>(
          `/companies/${companyId}/assets/images`,
          fd,
        );
        next.push({
          attachmentId: result.assetId,
          contentPath: `/api/assets/${result.assetId}/content`,
          source: "upload",
        });
      } catch (err) {
        alert(`Falha no upload: ${(err as Error).message}`);
      }
    }
    onChange(next);
  };
  return (
    <Field
      label="Posts / criativos de referência"
      hint={`${images.length} salvos. Os agentes (principalmente de arte) usam como gabarito visual. Suba os que você curte, ou raspe do @ do Instagram (em breve).`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleUpload(e.target.files)}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: 10,
        }}
      >
        {images.map((img, i) => (
          <AssetCard
            key={img.attachmentId}
            contentPath={img.contentPath}
            onRemove={() => onChange(images.filter((_, j) => j !== i))}
          >
            <input
              style={{ ...inputStyle, fontSize: 12, padding: "6px 8px" }}
              value={img.caption ?? ""}
              onChange={(e) => {
                const next = [...images];
                next[i] = { ...img, caption: e.target.value };
                onChange(next);
              }}
              placeholder="o que é / por que curtiu (opcional)"
            />
          </AssetCard>
        ))}
        <UploadCard onClick={() => inputRef.current?.click()} />
      </div>
    </Field>
  );
}

function ReferenceEditor({
  companyId,
  name,
  setName,
  description,
  setDescription,
  reference,
  setReference,
}: {
  companyId: string;
  name: string;
  setName: (n: string) => void;
  description: string;
  setDescription: (d: string) => void;
  reference: ReferenceMeta;
  setReference: (r: ReferenceMeta) => void;
}) {
  const rt = reference.referenceType ?? "custom";
  const upd = (patch: Partial<ReferenceMeta>) => setReference({ ...reference, ...patch });

  const images = reference.images ?? [];
  const [showTranscript, setShowTranscript] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div
        style={{
          display: "inline-flex",
          alignSelf: "flex-start",
          alignItems: "center",
          gap: 6,
          padding: "4px 12px",
          borderRadius: 999,
          background: "rgba(255,112,0,0.10)",
          color: "var(--nd-orange)",
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        <Lightbulb size={13} strokeWidth={2} /> Referência · {REFERENCE_TYPE_LABEL[rt]}
      </div>

      <Field label="Nome">
        <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Descrição" hint="Resumo curto, opcional">
        <input
          style={inputStyle}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      <ReferenceSectionTitle>Análise</ReferenceSectionTitle>

      {/* Comuns a todos os tipos */}
      <Field label="Por que é uma boa referência?" hint="O insight central: o que ela faz bem que vale seguir">
        <textarea
          style={textareaStyle}
          value={reference.porQueReferencia ?? ""}
          onChange={(e) => upd({ porQueReferencia: e.target.value })}
          placeholder="Ex: comunica autoridade sem ser arrogante; usa dados + história em cada post"
        />
      </Field>
      <Field label="Posicionamento" hint="O ângulo / visão de mundo que sustenta a referência">
        <textarea
          style={textareaStyle}
          value={reference.posicionamento ?? ""}
          onChange={(e) => upd({ posicionamento: e.target.value })}
          placeholder="Ex: 'mudança exige desconforto' — fala de transformação real, sem atalhos"
        />
      </Field>
      <Field label="Tom de voz">
        <input style={inputStyle} value={reference.tom ?? ""} onChange={(e) => upd({ tom: e.target.value })} placeholder="Ex: direto, provocador, sem jargão" />
      </Field>
      <Field label="Hooks / ganchos" hint="Aberturas que prendem a atenção">
        <ChipsInput value={reference.hooks ?? []} onChange={(v) => upd({ hooks: v })} placeholder="Ex: 'Todo mundo quer mudar, mas quase ninguém muda'" />
      </Field>

      {/* Autoridade */}
      {rt === "autoridade" && (
        <>
          <Field label="Por que tem autoridade" hint="O diagnóstico: o que faz essa pessoa/marca ser ouvida e respeitada">
            <textarea style={textareaStyle} value={reference.porQueAutoridade ?? ""} onChange={(e) => upd({ porQueAutoridade: e.target.value })} placeholder="Ex: fala de mercado com a vivência de quem construiu e quebrou empresas — credibilidade vem da cicatriz, não do diploma" />
          </Field>
          <Field label="Diferencial" hint="O que a distingue dos pares do nicho">
            <input style={inputStyle} value={reference.diferencial ?? ""} onChange={(e) => upd({ diferencial: e.target.value })} placeholder="Ex: junta dado duro com história pessoal crua" />
          </Field>
          <Field label="Pilares de conteúdo" hint="Os temas centrais que ela trabalha">
            <ChipsInput value={reference.pilares ?? []} onChange={(v) => upd({ pilares: v })} placeholder="Ex: gestão, processos, mentalidade" />
          </Field>
          <Field label="Sinais de credibilidade" hint="O que prova a autoridade (cases, números, vivência...)">
            <ChipsInput value={reference.sinais ?? []} onChange={(v) => upd({ sinais: v })} placeholder="Ex: 10 anos de mercado, +500 clientes" />
          </Field>
          <Field label="Gatilhos e persuasão" hint="Gatilhos mentais recorrentes">
            <ChipsInput value={reference.gatilhos ?? []} onChange={(v) => upd({ gatilhos: v })} placeholder="Ex: prova social, história pessoal, autoridade" />
          </Field>
        </>
      )}

      {/* Conteúdo */}
      {rt === "conteudo" && (
        <>
          <Field label="Formatos que funcionam" hint="Tipos de post/estrutura que engajam">
            <ChipsInput value={reference.formatos ?? []} onChange={(v) => upd({ formatos: v })} placeholder="Ex: carrossel mito x verdade, vídeo curto explicativo" />
          </Field>
          <Field label="Pilares / temas">
            <ChipsInput value={reference.pilares ?? []} onChange={(v) => upd({ pilares: v })} placeholder="Ex: dicas práticas, bastidores" />
          </Field>
          <Field label="Gatilhos e persuasão" hint="O que faz o conteúdo engajar/ser salvo">
            <ChipsInput value={reference.gatilhos ?? []} onChange={(v) => upd({ gatilhos: v })} placeholder="Ex: curiosidade, utilidade prática, polêmica" />
          </Field>
        </>
      )}

      {/* Criativo / Visual */}
      {rt === "criativo" && (
        <>
          <Field label="Estilo visual" hint="Descrição do visual: vibe, tratamento, mood">
            <textarea style={textareaStyle} value={reference.estiloVisual ?? ""} onChange={(e) => upd({ estiloVisual: e.target.value })} placeholder="Ex: minimalista, muito espaço em branco, fotografia clean" />
          </Field>
          <Field label="Paleta de cores">
            <ChipsInput value={reference.paleta ?? []} onChange={(v) => upd({ paleta: v })} placeholder="Ex: #0d1526, #fe7000, off-white" />
          </Field>
          <Field label="Layout / diagramação">
            <input style={inputStyle} value={reference.layout ?? ""} onChange={(e) => upd({ layout: e.target.value })} placeholder="Ex: título grande topo, imagem centro, CTA rodapé" />
          </Field>
          <Field label="Elementos recorrentes" hint="Elementos visuais que se repetem">
            <ChipsInput value={reference.elementosRecorrentes ?? []} onChange={(v) => upd({ elementosRecorrentes: v })} placeholder="Ex: moldura branca, tipografia grande, foto P&B" />
          </Field>
        </>
      )}

      {/* Sazonal / Campanha */}
      {rt === "sazonal" && (
        <>
          <Field label="Contexto da campanha">
            <input style={inputStyle} value={reference.contexto ?? ""} onChange={(e) => upd({ contexto: e.target.value })} placeholder="Ex: Black Friday, Dia das Mães" />
          </Field>
          <Field label="O que faz esses criativos funcionarem">
            <ChipsInput value={reference.oQueFunciona ?? []} onChange={(v) => upd({ oQueFunciona: v })} placeholder="Ex: senso de urgência, desconto em destaque, contraste alto" />
          </Field>
          <Field label="Gatilhos de urgência/conversão">
            <ChipsInput value={reference.gatilhos ?? []} onChange={(v) => upd({ gatilhos: v })} placeholder="Ex: contagem regressiva, 'últimas unidades', desconto progressivo" />
          </Field>
        </>
      )}

      {/* Narrativa — vem dos Reels transcritos (qualquer tipo) */}
      {(reference.estruturaNarrativa || (reference.bordoes?.length ?? 0) > 0 || (reference.transcricoes?.length ?? 0) > 0) && (
        <>
          <Field label="Estrutura narrativa" hint="Como a história é construída: gancho → desenvolvimento → CTA">
            <textarea
              style={textareaStyle}
              value={reference.estruturaNarrativa ?? ""}
              onChange={(e) => upd({ estruturaNarrativa: e.target.value })}
              placeholder="Ex: abre com afirmação provocadora, percorre fatos em ritmo acelerado, fecha com conselho prático"
            />
          </Field>
          <Field label="Bordões / vocabulário" hint="Frases e expressões de assinatura">
            <ChipsInput value={reference.bordoes ?? []} onChange={(v) => upd({ bordoes: v })} placeholder="Ex: 'pague o preço', 'não tem atalho'" />
          </Field>
        </>
      )}

      <Field label="O que aplicar nas suas tarefas" hint="A ponte prática: o que extrair pra usar (sem copiar)">
        <textarea
          style={textareaStyle}
          value={reference.oQueAplicar ?? ""}
          onChange={(e) => upd({ oQueAplicar: e.target.value })}
          placeholder="Ex: abrir com um dado forte, fechar com 1 ação prática"
        />
      </Field>

      {/* ── Material de apoio (matéria-prima, não o ouro) ── */}
      <ReferenceSectionTitle>Material de apoio</ReferenceSectionTitle>

      <ReferenceImagesGrid
        companyId={companyId}
        images={images}
        onChange={(imgs) => upd({ images: imgs })}
      />

      {(reference.transcricoes?.length ?? 0) > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowTranscript((s) => !s)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: 0,
              border: "none",
              background: "transparent",
              color: "var(--wf-ink-2)",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--wf-font-body)",
            }}
          >
            <ChevronRight
              size={14}
              strokeWidth={2}
              style={{ transform: showTranscript ? "rotate(90deg)" : "none", transition: "transform .15s" }}
            />
            Transcrição dos Reels ({reference.transcricoes!.length})
          </button>
          <p style={{ fontSize: 11, color: "var(--wf-ink-3)", margin: "4px 0 0 20px", lineHeight: 1.5 }}>
            Matéria-prima pra storytelling. O ouro é a Análise acima — isso fica de apoio pros agentes.
          </p>
          {showTranscript && (
            <div
              style={{
                marginTop: 8,
                maxHeight: 260,
                overflowY: "auto",
                padding: "10px 12px",
                borderRadius: 8,
                background: "var(--wf-paper)",
                border: "1px solid var(--wf-ink-5)",
                fontSize: 12,
                lineHeight: 1.6,
                color: "var(--wf-ink-2)",
                whiteSpace: "pre-wrap",
              }}
            >
              {reference.transcricoes!.join("\n\n———\n\n")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SimpleConteudoTab({
  name,
  setName,
  description,
  setDescription,
  contentText,
  setContentText,
}: {
  name: string;
  setName: (n: string) => void;
  description: string;
  setDescription: (d: string) => void;
  contentText: string;
  setContentText: (t: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <Field label="Nome">
        <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Descrição">
        <input
          style={inputStyle}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field
        label="Conteúdo"
        hint={`${contentText.length.toLocaleString("pt-BR")} caracteres. texto que vai pro contexto do agente.`}
      >
        <textarea
          style={{ ...textareaStyle, minHeight: 240 }}
          value={contentText}
          onChange={(e) => setContentText(e.target.value)}
        />
      </Field>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Competitor view — KB de concorrente (mesma vibe das abas de produto)
// ─────────────────────────────────────────────────────────────


function CompetitorEmpty({ msg }: { msg: string }) {
  return (
    <div className="wf-hand-note" style={{ fontSize: 14, color: "var(--wf-ink-3)", padding: "8px 2px" }}>
      {msg}
    </div>
  );
}

function fmtNum(n?: number): string {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return n.toLocaleString("pt-BR");
}

function CompetitorProfileCard({ c }: { c: CompetitorMeta }) {
  const p = c.profile;
  // Asset persistido vence as URLs cruas (IG CDN expira em ~24h + CORS).
  const avatar = p?.profilePicContentPath || p?.profilePicUrlHD || p?.profilePicUrl;
  const name = p?.fullName || (c.handle ? `@${c.handle}` : c.brandQuery || "Concorrente");
  const handleLine = p?.username ? `@${p.username}` : c.handle ? `@${c.handle}` : null;
  const site = p?.externalUrl || c.sourceUrl;
  return (
    <div style={{ padding: 18, background: "#fafaf6", borderRadius: 14, display: "flex", gap: 16 }}>
      {avatar ? (
        <img
          src={avatar}
          alt=""
          referrerPolicy="no-referrer"
          style={{ width: 72, height: 72, borderRadius: 14, objectFit: "cover", flexShrink: 0 }}
        />
      ) : (
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 14,
            background: "var(--wf-ink-5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Target size={26} color="var(--wf-ink-3)" />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="wf-display" style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
          {name}
        </div>
        {handleLine && (
          <div style={{ fontSize: 12, color: "var(--wf-ink-3)" }}>{handleLine}</div>
        )}
        {p?.biography && (
          <div style={{ fontSize: 12.5, color: "var(--wf-ink-2)", marginTop: 6, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
            {p.biography}
          </div>
        )}
        <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
          {typeof p?.followersCount === "number" && (
            <span style={{ fontSize: 12 }}>
              <strong>{fmtNum(p.followersCount)}</strong>{" "}
              <span style={{ color: "var(--wf-ink-3)" }}>seguidores</span>
            </span>
          )}
          {typeof p?.postsCount === "number" && (
            <span style={{ fontSize: 12 }}>
              <strong>{fmtNum(p.postsCount)}</strong>{" "}
              <span style={{ color: "var(--wf-ink-3)" }}>posts</span>
            </span>
          )}
          {typeof c.ads?.length === "number" && c.ads.length > 0 && (
            <span style={{ fontSize: 12 }}>
              <strong>{c.ads.length}</strong>{" "}
              <span style={{ color: "var(--wf-ink-3)" }}>anúncios ativos</span>
            </span>
          )}
          {site && (
            <a
              href={site}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 12, color: "var(--nd-orange-pun)", textDecoration: "underline" }}
            >
              {site.replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </a>
          )}
        </div>
        {c.scrapedAt && (
          <div style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: 8 }}>
            coletado em {formatRelative(c.scrapedAt)}
          </div>
        )}
      </div>
    </div>
  );
}

function CompetitorDossieTab({
  c,
  name,
  setName,
  description,
  setDescription,
}: {
  c: CompetitorMeta;
  name: string;
  setName: (n: string) => void;
  description: string;
  setDescription: (d: string) => void;
}) {
  const sections = splitMdSections(c.dossier || "");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      <CompetitorProfileCard c={c} />
      <Field label="Nome desta base">
        <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Descrição">
        <input style={inputStyle} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      {!c.dossier?.trim() ? (
        <CompetitorEmpty msg="O dossiê ainda não foi gerado pra esse concorrente." />
      ) : (
        sections.map((s, i) => (
          <div key={i}>
            {s.title && (
              <div
                className="wf-display"
                style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, marginBottom: 10, color: "var(--nd-orange-pun)" }}
              >
                {s.title}
              </div>
            )}
            <MarkdownLite body={s.body} />
          </div>
        ))
      )}
    </div>
  );
}

function CompetitorNumbersTab({ c }: { c: CompetitorMeta }) {
  const posts = c.profile?.latestPosts ?? [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      <CompetitorProfileCard c={c} />
      <div>
        <div
          className="wf-display"
          style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, marginBottom: 14, color: "var(--nd-orange-pun)" }}
        >
          Posts recentes ({posts.length})
        </div>
        {posts.length === 0 ? (
          <CompetitorEmpty msg="Sem posts coletados." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {posts.map((post, i) => {
              // Asset persistido vence URL crua (IG CDN expira).
              const thumb = (post.contentPath as string | undefined) || (post.displayUrl as string | undefined);
              const cap = (post.caption as string | undefined) ?? "";
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: 10,
                    background: "#fafaf6",
                    borderRadius: 8,
                  }}
                >
                  {thumb && (
                    <img
                      src={thumb}
                      alt=""
                      referrerPolicy="no-referrer"
                      style={{ width: 56, height: 56, borderRadius: 6, objectFit: "cover", flexShrink: 0 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--wf-ink-2)",
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {cap || <span style={{ color: "var(--wf-ink-3)" }}>(sem legenda)</span>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4, fontSize: 11, color: "var(--wf-ink-3)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Heart size={11} strokeWidth={2} /> {fmtNum(post.likesCount as number | undefined)}
                      </span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <MessageCircle size={11} strokeWidth={2} /> {fmtNum(post.commentsCount as number | undefined)}
                      </span>
                      {post.url && (
                        <a
                          href={post.url as string}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "var(--nd-orange-pun)", textDecoration: "underline" }}
                        >
                          ver post
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** CTAs default do Meta Ads que não dizem nada sobre a oferta (são botão
 *  padrão de plataforma). Quando o anúncio só tem um desses, escondemos o
 *  pill — informação visual zero, vira ruído. */
const GENERIC_CTA_TEXTS = new Set([
  "learn more",
  "see details",
  "see more",
  "shop now",
  "sign up",
  "subscribe",
  "apply now",
  "get offer",
  "get quote",
  "download",
  "book now",
  "contact us",
  "send message",
  "play game",
  "watch more",
  "listen now",
  "saiba mais",
  "ver mais",
  "ver detalhes",
  "comprar",
  "comprar agora",
  "cadastre-se",
  "inscreva-se",
  "inscrever-se",
  "fazer download",
  "agendar",
  "entre em contato",
  "enviar mensagem",
]);

function isGenericCta(cta?: string): boolean {
  return !!cta && GENERIC_CTA_TEXTS.has(cta.trim().toLowerCase());
}

function CompetitorCreativesTab({ c }: { c: CompetitorMeta }) {
  const creatives = c.topCreatives ?? [];
  // Filtro client-side: o actor da Meta Ads Library às vezes retorna entradas
  // só com { url, error, errorCode } (página sem ads ou ID inválido). KBs
  // antigas (analisadas antes do filtro server-side) ainda têm isso no DB —
  // dropa aqui pra não mostrar card vazio.
  const ads = (c.ads ?? []).filter((ad) => {
    if ((ad as { error?: unknown }).error) return false;
    const hasMedia = (ad.imageUrls?.length ?? 0) > 0 || (ad.videoUrls?.length ?? 0) > 0 || !!ad.thumbUrl || !!ad.contentPath;
    const hasContent = !!ad.text?.trim() || !!ad.ctaText?.trim();
    return hasMedia || hasContent;
  });
  const [openAdIdx, setOpenAdIdx] = useState<number | null>(null);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      <div>
        <div
          className="wf-display"
          style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, marginBottom: 14, color: "var(--nd-orange-pun)" }}
        >
          Criativos que mais engajaram ({creatives.length})
        </div>
        {creatives.length === 0 ? (
          <CompetitorEmpty msg="Sem criativos coletados." />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: 10,
            }}
          >
            {creatives.map((cr, i) => {
              // contentPath = asset permanente (URLs cruas de IG/Meta expiram).
              const img = cr.contentPath || cr.thumbUrl || cr.url;
              return (
                <a
                  key={i}
                  href={cr.sourceUrl || cr.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    borderRadius: 10,
                    overflow: "hidden",
                    textDecoration: "none",
                    color: "inherit",
                    background: "#fafaf6",
                  }}
                >
                  {img ? (
                    <img
                      src={img}
                      alt=""
                      referrerPolicy="no-referrer"
                      style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <div style={{ width: "100%", aspectRatio: "1/1", background: "var(--wf-ink-5)" }} />
                  )}
                  <div style={{ padding: 8 }}>
                    <div style={{ fontSize: 10, color: "var(--wf-ink-3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {cr.kind === "ad" ? "Anúncio" : "Post"}
                      {cr.metricValue != null && ` · ${fmtNum(cr.metricValue)} ${cr.metricLabel ?? ""}`}
                    </div>
                    {cr.caption && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--wf-ink-2)",
                          marginTop: 3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.4,
                        }}
                      >
                        {cr.caption}
                      </div>
                    )}
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div
          className="wf-display"
          style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, marginBottom: 14, color: "var(--nd-orange-pun)" }}
        >
          Anúncios ativos na Meta Ads Library ({ads.length})
        </div>
        {ads.length === 0 ? (
          <CompetitorEmpty msg="Nenhum anúncio ativo encontrado (ou a marca não anuncia na Meta)." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ads.map((ad, i) => {
              // Asset persistido vence URLs cruas (Meta CDN tem CORS bloqueando).
              const img = ad.contentPath || ad.imageUrls?.[0] || ad.thumbUrl;
              const adLink = ad.snapshotUrl || ad.ctaUrl;
              const ctaToShow = isGenericCta(ad.ctaText) ? undefined : ad.ctaText;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setOpenAdIdx(i)}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: 12,
                    background: "#fafaf6",
                    borderRadius: 8,
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  {img && (
                    <img
                      src={img}
                      alt=""
                      referrerPolicy="no-referrer"
                      style={{ width: 72, height: 72, borderRadius: 6, objectFit: "cover", flexShrink: 0 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {ad.text && (
                      <div style={{ fontSize: 12, color: "var(--wf-ink-2)", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                        {ad.text.slice(0, 240)}{ad.text.length > 240 ? "…" : ""}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 11, color: "var(--wf-ink-3)", flexWrap: "wrap", alignItems: "center" }}>
                      {ctaToShow && (
                        <span
                          className="wf-pill"
                          style={{ background: "var(--wf-ink-6, rgba(0,0,0,0.05))", padding: "2px 8px", borderRadius: 20 }}
                        >
                          {ctaToShow}
                        </span>
                      )}
                      {adLink && (
                        <span style={{ color: "var(--nd-orange-pun)" }}>
                          {ad.snapshotUrl ? "ver na biblioteca de anúncios" : adLink.replace(/^https?:\/\//, "").slice(0, 50)}
                        </span>
                      )}
                      {ad.startDate && <span>desde {ad.startDate}</span>}
                      {ad.isActive === false && <span style={{ color: "var(--wf-ink-3)" }}>(inativo)</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <AdLightbox ads={ads} index={openAdIdx} onClose={() => setOpenAdIdx(null)} onChange={setOpenAdIdx} />
    </div>
  );
}

/** Lightbox para anúncio: imagem grande à esquerda, copy completa à direita,
 *  CTA + link da Biblioteca de Anúncios da Meta. Esc/setas navegam. */
function AdLightbox({
  ads,
  index,
  onClose,
  onChange,
}: {
  ads: CompetitorAd[];
  index: number | null;
  onClose: () => void;
  onChange: (next: number) => void;
}) {
  const isOpen = index !== null && ads.length > 0;
  const total = ads.length;
  const safeIdx = index ?? 0;

  const goPrev = useCallback(() => {
    if (total === 0) return;
    onChange((safeIdx - 1 + total) % total);
  }, [safeIdx, total, onChange]);
  const goNext = useCallback(() => {
    if (total === 0) return;
    onChange((safeIdx + 1) % total);
  }, [safeIdx, total, onChange]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose, goPrev, goNext]);

  if (!isOpen) return null;
  const ad = ads[safeIdx]!;
  const img = ad.contentPath || ad.imageUrls?.[0] || ad.thumbUrl;
  const adLink = ad.snapshotUrl || ad.ctaUrl;
  const ctaToShow = isGenericCta(ad.ctaText) ? undefined : ad.ctaText;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(10, 12, 20, 0.75)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        padding: 24,
      }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Fechar"
        style={{
          position: "absolute", top: 18, right: 18, width: 40, height: 40,
          borderRadius: 999, border: "none", background: "rgba(255,255,255,0.12)",
          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <X size={18} />
      </button>

      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label="Anterior"
          style={{
            position: "absolute", left: 18, top: "50%", transform: "translateY(-50%)",
            width: 48, height: 48, borderRadius: 999, border: "none",
            background: "rgba(255,255,255,0.12)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }}
        >
          <ChevronLeft size={22} />
        </button>
      )}

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          gap: 0,
          background: "#fff",
          borderRadius: 12,
          overflow: "hidden",
          maxWidth: "min(95vw, 1100px)",
          width: "100%",
          maxHeight: "88vh",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ flex: "0 0 50%", background: "#000", display: "flex", alignItems: "center", justifyContent: "center", maxHeight: "88vh" }}>
          {img ? (
            <img
              src={img}
              alt=""
              referrerPolicy="no-referrer"
              style={{ width: "100%", height: "100%", maxHeight: "88vh", objectFit: "contain", display: "block" }}
            />
          ) : (
            <div style={{ color: "#666", fontSize: 13 }}>(sem imagem)</div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, padding: 24, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          {ad.pageName && (
            <div style={{ fontSize: 13, color: "var(--wf-ink-3)", fontWeight: 600 }}>{ad.pageName}</div>
          )}
          {ad.text && (
            <div style={{ fontSize: 14, color: "var(--wf-ink)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {ad.text}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12, color: "var(--wf-ink-3)" }}>
            {ctaToShow && (
              <span style={{ background: "rgba(0,0,0,0.06)", padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>
                {ctaToShow}
              </span>
            )}
            {ad.startDate && <span>desde {ad.startDate}</span>}
            {ad.isActive === false && <span>(inativo)</span>}
          </div>
          {adLink && (
            <a
              href={adLink}
              target="_blank"
              rel="noreferrer"
              style={{ color: "var(--nd-orange-pun)", textDecoration: "underline", fontSize: 13 }}
            >
              {ad.snapshotUrl ? "Ver na Biblioteca de Anúncios da Meta →" : adLink.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </div>

      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label="Próximo"
          style={{
            position: "absolute", right: 18, top: "50%", transform: "translateY(-50%)",
            width: 48, height: 48, borderRadius: 999, border: "none",
            background: "rgba(255,255,255,0.12)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }}
        >
          <ChevronRight size={22} />
        </button>
      )}

      <div
        style={{
          position: "absolute", bottom: 22, left: 0, right: 0,
          display: "flex", justifyContent: "center", pointerEvents: "none",
        }}
      >
        <div style={{ padding: "6px 12px", borderRadius: 999, background: "rgba(0,0,0,0.45)", color: "rgba(255,255,255,0.9)", fontSize: 12, fontWeight: 700 }}>
          {safeIdx + 1}/{total}
        </div>
      </div>
    </div>
  );
}

function CompetitorSiteTab({ c }: { c: CompetitorMeta }) {
  const lp = c.landingPage;
  if (!lp) return <CompetitorEmpty msg="Não foi possível raspar o site do concorrente (ou o link na bio não tem conteúdo útil — tipo Linktree ou página só com links)." />;
  const header = (
    <div>
      <div className="wf-display" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, marginBottom: 6, color: "var(--nd-orange-pun)" }}>
        {lp.title || "Página do concorrente"}
      </div>
      <a
        href={lp.url}
        target="_blank"
        rel="noreferrer"
        style={{ fontSize: 12, color: "var(--nd-orange-pun)", textDecoration: "underline" }}
      >
        {lp.url}
      </a>
    </div>
  );

  // KB antiga: analysis é string (markdown). Renderiza via MarkdownLite.
  if (typeof lp.analysis === "string") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {header}
        <MarkdownLite body={lp.analysis} />
      </div>
    );
  }

  const a = lp.analysis;
  // Sem análise estruturada ainda — mostra markdown bruto como fallback final.
  if (!a) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {header}
        <MarkdownLite body={lp.markdown || ""} />
      </div>
    );
  }

  if (a.observacao && !a.promessaPrincipal && !a.headlinePrincipal) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {header}
        <CompetitorEmpty msg={a.observacao} />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {header}

      <CompetitorSiteSection title="Oferta">
        {a.promessaPrincipal && <ReadField label="Promessa central" value={a.promessaPrincipal} />}
        {a.publicoAlvo && <ReadField label="Público-alvo" value={a.publicoAlvo} />}
        {(a.tipoOferta || a.formato) && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {a.tipoOferta && <ReadField label="Tipo de oferta" value={a.tipoOferta} />}
            {a.formato && <ReadField label="Formato" value={a.formato} />}
          </div>
        )}
        {(a.preco || a.garantia) && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {a.preco && <ReadField label="Preço" value={a.preco} />}
            {a.garantia && <ReadField label="Garantia" value={a.garantia} />}
          </div>
        )}
      </CompetitorSiteSection>

      <CompetitorSiteSection title="Como apresenta">
        {a.headlinePrincipal && <ReadField label="Headline principal" value={a.headlinePrincipal} />}
        {a.provaSocial && <ReadField label="Prova social" value={a.provaSocial} />}
        {a.ctaPrincipal && <ReadField label="CTA principal" value={a.ctaPrincipal} />}
      </CompetitorSiteSection>

      {(a.forcas?.length || a.fraquezas?.length) ? (
        <CompetitorSiteSection title="Sinais">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {a.forcas?.length ? <ReadBullets label="Forças" items={a.forcas} accent="#0a7f3f" /> : <div />}
            {a.fraquezas?.length ? <ReadBullets label="Fraquezas" items={a.fraquezas} accent="#b54708" /> : <div />}
          </div>
        </CompetitorSiteSection>
      ) : null}
    </div>
  );
}

function CompetitorSiteSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--nd-orange-pun)", fontFamily: "var(--wf-font-display)" }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, background: "#fafaf6", borderRadius: 14, padding: 18 }}>
        {children}
      </div>
    </div>
  );
}

function ReadField({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--wf-ink-2)", lineHeight: 1.55 }}>{value}</span>
    </div>
  );
}

function ReadBullets({ label, items, accent }: { label: string; items: string[]; accent: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wf-ink)" }}>{label}</span>
      <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((it, i) => (
          <li key={i} style={{ fontSize: 13, color: "var(--wf-ink-2)", lineHeight: 1.5, paddingLeft: 14, position: "relative" }}>
            <span style={{ position: "absolute", left: 0, top: 6, width: 6, height: 6, borderRadius: 3, background: accent }} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompetitorView({
  kb,
  name,
  setName,
  description,
  setDescription,
  tab,
}: {
  kb: KnowledgeBase;
  name: string;
  setName: (n: string) => void;
  description: string;
  setDescription: (d: string) => void;
  tab: CompetitorTabKey;
}) {
  const c = (kb.contentMeta as KnowledgeBaseContentMeta).competitor;
  if (!c) {
    return (
      <SimpleConteudoTab
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        contentText={kb.contentText}
        setContentText={() => {}}
      />
    );
  }
  if (tab === "perfil") return <CompetitorNumbersTab c={c} />;
  if (tab === "criativos") return <CompetitorCreativesTab c={c} />;
  if (tab === "site") return <CompetitorSiteTab c={c} />;
  return (
    <CompetitorDossieTab
      c={c}
      name={name}
      setName={setName}
      description={description}
      setDescription={setDescription}
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────

export default function KbDetail() {
  const { kbId } = useParams<{ kbId: string }>();
  const navigate = useNavigate();
  const goBack = useSmartBack("/briefing");
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState | null>(null);
  // Timestamp do último save bem-sucedido. Usado pra mostrar "Salvo às HH:MM"
  // por 3s depois do save antes de voltar pro "tudo salvo" neutro — feedback
  // claro que o clique funcionou.
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tab, setTab] = useState<AnyTab>("identidade");

  // Reconciliação pós-save: sem isso, campos derivados (ex.: contentText de
  // referência, re-renderizado a partir de `reference` no save) round-trip
  // DIFERENTES do form carregado → `dirty` fica true pra sempre → o auto-save
  // dispara em loop a cada ~2s e o botão "Salvar" pisca. `formRef` segue o form
  // atual; `savingSnapshotRef` guarda o form no instante do save.
  const formRef = useRef<FormState | null>(form);
  formRef.current = form;
  const savingSnapshotRef = useRef<string | null>(null);

  const kbQuery = useQuery({
    queryKey: companyId && kbId ? queryKeys.companies.knowledgeOne(companyId, kbId) : ["kb-none"],
    queryFn: () => knowledgeApi.get(companyId!, kbId!),
    enabled: !!companyId && !!kbId,
  });

  const kb = kbQuery.data ?? null;

  useEffect(() => {
    if (kb && !form) {
      setForm(fromKb(kb));
      // Tab inicial depende do tipo
      setTab(
        kb.type === "product"
          ? "identidade"
          : kb.type === "competitor" &&
              (kb.contentMeta as KnowledgeBaseContentMeta)?.competitor
            ? "dossie"
            : "conteudo",
      );
    }
  }, [kb, form]);

  const update = useMutation({
    mutationFn: () => {
      if (!companyId || !kbId || !form || !kb) throw new Error("missing");
      // Snapshot do form no instante do save — pra saber, no onSuccess, se o
      // usuário editou durante o save em voo.
      savingSnapshotRef.current = JSON.stringify(form);
      const isProduct = kb.type === "product";
      const isReference = kb.type === "reference";
      return knowledgeApi.update(companyId, kbId, {
        name: form.name,
        description: form.description || null,
        contentText: isProduct
          ? undefined
          : isReference
            ? renderReferenceContentText(form.reference)
            : form.contentText,
        contentMeta: isProduct
          ? {
              ...((kb.contentMeta as object | null) ?? {}),
              product: form.product,
              // Brand library — paleta/fontes/lettering/tom + fotos do
              // expert + criativos. Campos só persistem se tiverem valor
              // (sem isso, save sobrescreveria com {} mesmo sem edição).
              ...(Object.keys(form.brandIdentity ?? {}).length > 0
                ? { brandIdentity: form.brandIdentity }
                : { brandIdentity: undefined }),
              peopleLibrary: form.peopleLibrary,
              creativeLibrary: form.creativeLibrary,
              logos: form.logos,
            }
          : isReference
            ? {
                ...((kb.contentMeta as object | null) ?? {}),
                reference: form.reference,
              }
            : kb.contentMeta,
      });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.companies.knowledgeOne(companyId!, kbId!), updated);
      queryClient.invalidateQueries({
        queryKey: companyId ? queryKeys.companies.knowledge(companyId) : [],
      });
      // Reconcilia o form com o retorno do servidor SÓ se o usuário não editou
      // durante o save em voo (snapshot == form atual). Isso zera o `dirty`
      // mesmo quando o servidor normaliza/deriva campos (ex.: contentText de
      // referência) — sem isso o auto-save entrava em loop e o botão piscava.
      // Se houve edição em voo, mantém o form e deixa o auto-save re-disparar
      // (preserva as teclas novas).
      if (
        savingSnapshotRef.current !== null &&
        formRef.current &&
        JSON.stringify(formRef.current) === savingSnapshotRef.current
      ) {
        setForm(fromKb(updated));
      }
      setLastSavedAt(new Date());
    },
  });

  // Reseta indicador "Salvo às HH:MM" depois de 3s — passa a ser só
  // "tudo salvo" pra não poluir.
  useEffect(() => {
    if (!lastSavedAt) return;
    const t = setTimeout(() => setLastSavedAt(null), 3000);
    return () => clearTimeout(t);
  }, [lastSavedAt]);

  const del = useMutation({
    mutationFn: () => {
      if (!companyId || !kbId) throw new Error("missing");
      return knowledgeApi.delete(companyId, kbId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyId ? queryKeys.companies.knowledge(companyId) : [],
      });
      navigate("/briefing");
    },
  });

  const reanalyze = useMutation({
    mutationFn: () => {
      if (!companyId || !kbId) throw new Error("missing");
      return knowledgeApi.reanalyze(companyId, kbId);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.companies.knowledgeOne(companyId!, kbId!), updated);
      queryClient.invalidateQueries({
        queryKey: companyId ? queryKeys.companies.knowledge(companyId) : [],
      });
      setForm(fromKb(updated));
    },
  });

  const dirty = useMemo(() => kb && form && isDirty(form, fromKb(kb)), [form, kb]);

  // Auto-save: 2s depois da última edição, persiste sozinho. Resolve a dor #1
  // do beta (trocar de aba e perder tudo). O botão "Salvar" continua como ação
  // explícita (redundância intencional). `form` nas deps = debounce reinicia a
  // cada tecla; só agenda quando há mudança real e nenhum save em voo.
  useEffect(() => {
    if (!dirty || update.isPending) return;
    const t = setTimeout(() => update.mutate(), 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dirty, update.isPending]);

  if (kb?.type === "onboarding") {
    return <Navigate to="/briefing/onboarding" replace />;
  }

  if (kbQuery.isLoading) {
    return (
      <div style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando base…</span>
      </div>
    );
  }

  if (kbQuery.isError || !kb || !form) {
    return (
      <div style={{ padding: 32, maxWidth: 720, margin: "0 auto" }}>
        <button
          type="button"
          className="wf-btn wf-btn--ghost wf-btn--sm"
          onClick={goBack}
          style={{ marginBottom: 16 }}
        >
          <ArrowLeft size={12} strokeWidth={2} /> Voltar
        </button>
        <div className="wf-card" style={{ padding: 18 }}>
          Base não encontrada ou sem acesso.
        </div>
      </div>
    );
  }

  const meta = TYPE_META[kb.type];
  const Icon = meta.icon;
  const isProduct = kb.type === "product";
  const competitorMeta =
    kb.type === "competitor"
      ? (kb.contentMeta as KnowledgeBaseContentMeta)?.competitor ?? null
      : null;
  const isCompetitor = !!competitorMeta;
  const isReference = kb.type === "reference";
  const competitorTabs: { key: AnyTab; label: string }[] = [
    { key: "dossie", label: "Dossiê" },
    { key: "perfil", label: "Perfil & números" },
    { key: "criativos", label: "Criativos & anúncios" },
    ...(competitorMeta?.landingPage ? [{ key: "site" as AnyTab, label: "Site" }] : []),
  ];

  return (
    <div>
      {/* Overlay análise */}
      {reanalyze.isPending && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(5,8,16,0.78)",
            backdropFilter: "blur(8px)",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
          }}
        >
          <LoadingPlanet size={120} label="Reanalisando o produto…" />
        </motion.div>
      )}

      {/* Hero */}
      <div
        style={{
          background: "linear-gradient(90deg, #050810 0%, #0a1020 50%, #1a1424 100%)",
          color: "#fff",
          padding: "28px 32px 24px",
          borderBottom: "1px solid var(--wf-ink-5)",
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <button
            type="button"
            onClick={() => navigate("/briefing")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              padding: "4px 10px 4px 6px",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 999,
              background: "rgba(255,255,255,0.03)",
              color: "rgba(255,255,255,0.7)",
              cursor: "pointer",
              fontFamily: "var(--wf-font-body)",
              marginBottom: 16,
            }}
          >
            <ArrowLeft size={12} strokeWidth={2} /> Bases
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: `${meta.color}22`,
                color: meta.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon size={20} strokeWidth={1.75} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.55)",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  marginBottom: 2,
                }}
              >
                {meta.label} · atualizada {formatRelative(kb.updatedAt)}
              </div>
              <h1
                className="wf-display"
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.15,
                  margin: 0,
                  // Nome longo QUEBRA em linhas em vez de estourar a caixa
                  // (antes era nowrap+ellipsis e vazava quando o container não
                  // limitava a largura). overflowWrap:anywhere quebra até string
                  // única gigante sem espaço.
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {form.name || "(sem nome)"}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <TabsBar<AnyTab>
        tabs={isProduct ? PRODUCT_TABS : isCompetitor ? competitorTabs : SIMPLE_TABS}
        current={tab}
        onChange={setTab}
      />

      {/* Body */}
      <div style={{ padding: "24px 32px 100px" }}>
        <div
          style={{
            maxWidth: 880,
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {kb.status === "failed" && kb.statusMessage && (
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                padding: "14px 16px",
                background: "#fff0ee",
                border: "1px solid #ff5577",
                borderRadius: 10,
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "rgba(255,85,119,0.16)",
                  color: "#d92d4a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AlertTriangle size={17} strokeWidth={2.25} />
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--wf-ink)" }}>
                  Algo deu errado na análise
                </span>
                <span style={{ fontSize: 12, color: "var(--wf-ink-2)", lineHeight: 1.5 }}>
                  {kb.statusMessage}
                </span>
              </div>
            </div>
          )}

          {kb.contentMeta?.source && (
            <div
              style={{
                padding: "10px 14px",
                background: "var(--wf-paper)",
                border: "1px dashed var(--wf-ink-4)",
                borderRadius: 8,
                fontSize: 12,
                color: "var(--wf-ink-2)",
                display: "flex",
                gap: 10,
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 600 }}>Fonte:</span>
              {kb.contentMeta.source.originalUrl && (
                <a
                  href={kb.contentMeta.source.originalUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: "var(--nd-orange-pun)",
                    textDecoration: "underline",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {kb.contentMeta.source.originalUrl}
                </a>
              )}
              {!kb.contentMeta.source.originalUrl &&
                kb.contentMeta.source.originalFilename && (
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      color: "var(--wf-ink)",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {kb.contentMeta.source.originalFilename}
                    {kb.contentMeta.source.byteSize && (
                      <span style={{ color: "var(--wf-ink-3)", fontWeight: 400 }}>
                        {" · "}
                        {(
                          (kb.contentMeta.source.byteSize as number) / 1024
                        ).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}{" "}
                        KB
                      </span>
                    )}
                  </span>
                )}
              {isProduct && kb.contentMeta.source.originalUrl && (
                <button
                  type="button"
                  onClick={() => reanalyze.mutate()}
                  disabled={reanalyze.isPending}
                  className="wf-btn wf-btn--ghost wf-btn--sm"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    flexShrink: 0,
                  }}
                  title="Roda a análise de novo a partir da URL"
                >
                  <RefreshCw
                    size={11}
                    strokeWidth={2}
                    className={reanalyze.isPending ? "wf-spin" : undefined}
                  />
                  Reanalisar
                </button>
              )}
            </div>
          )}

          {/* Tab content */}
          {isProduct ? (
            <>
              {tab === "identidade" && (
                <ProductIdentidadeTab
                  product={form.product}
                  setProduct={(p) => setForm({ ...form, product: p })}
                  name={form.name}
                  setName={(n) => setForm({ ...form, name: n })}
                  description={form.description}
                  setDescription={(d) => setForm({ ...form, description: d })}
                />
              )}
              {tab === "oferta" && (
                <ProductOfertaTab
                  product={form.product}
                  setProduct={(p) => setForm({ ...form, product: p })}
                />
              )}
              {tab === "beneficios" && (
                <ProductBeneficiosTab
                  product={form.product}
                  setProduct={(p) => setForm({ ...form, product: p })}
                />
              )}
              {tab === "publico" && (
                <ProductPublicoTab
                  product={form.product}
                  setProduct={(p) => setForm({ ...form, product: p })}
                />
              )}
              {tab === "mercado" && (
                <ProductMercadoTab
                  product={form.product}
                  setProduct={(p) => setForm({ ...form, product: p })}
                />
              )}
              {tab === "visual" && companyId && kb && (
                <ProductVisualTab
                  companyId={companyId}
                  productKbId={kb.id}
                  brand={form.brandIdentity}
                  people={form.peopleLibrary}
                  creatives={form.creativeLibrary}
                  logos={form.logos}
                  onBrandChange={(next) =>
                    setForm({ ...form, brandIdentity: next })
                  }
                  onPeopleChange={(next) =>
                    setForm({ ...form, peopleLibrary: next })
                  }
                  onCreativesChange={(next) =>
                    setForm({ ...form, creativeLibrary: next })
                  }
                  onLogosChange={(next) => setForm({ ...form, logos: next })}
                />
              )}
            </>
          ) : isCompetitor ? (
            <CompetitorView
              kb={kb}
              name={form.name}
              setName={(n) => setForm({ ...form, name: n })}
              description={form.description}
              setDescription={(d) => setForm({ ...form, description: d })}
              tab={
                (["dossie", "perfil", "criativos", "site"].includes(tab)
                  ? tab
                  : "dossie") as CompetitorTabKey
              }
            />
          ) : isReference ? (
            <ReferenceEditor
              companyId={companyId!}
              name={form.name}
              setName={(n) => setForm({ ...form, name: n })}
              description={form.description}
              setDescription={(d) => setForm({ ...form, description: d })}
              reference={form.reference}
              setReference={(r) => setForm({ ...form, reference: r })}
            />
          ) : (
            <>
              {tab === "conteudo" && (
                <SimpleConteudoTab
                  name={form.name}
                  setName={(n) => setForm({ ...form, name: n })}
                  description={form.description}
                  setDescription={(d) => setForm({ ...form, description: d })}
                  contentText={form.contentText}
                  setContentText={(t) => setForm({ ...form, contentText: t })}
                />
              )}
            </>
          )}

          {/* Sticky action bar */}
          <div
            style={{
              position: "sticky",
              bottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 16px",
              marginTop: 16,
              background: "var(--wf-paper)",
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 12,
              boxShadow: "0 8px 24px rgba(5,8,16,0.08)",
              zIndex: 10,
            }}
          >
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="wf-btn wf-btn--ghost wf-btn--sm"
              style={{ display: "flex", alignItems: "center", gap: 6, color: "#cc3344" }}
            >
              <Trash2 size={13} strokeWidth={1.75} /> Deletar
            </button>
            <div style={{ flex: 1 }} />
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                color: lastSavedAt
                  ? "#1a8754"
                  : dirty
                    ? "var(--nd-orange)"
                    : "var(--wf-ink-3)",
                fontWeight: lastSavedAt ? 600 : 400,
              }}
            >
              {lastSavedAt ? (
                <>
                  <Check size={12} strokeWidth={2.25} />
                  {`Salvo às ${lastSavedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`}
                </>
              ) : dirty ? (
                "alterações não salvas"
              ) : (
                "tudo salvo"
              )}
            </span>
            <button
              type="button"
              onClick={() => update.mutate()}
              disabled={!dirty || update.isPending}
              className="wf-btn wf-btn--primary"
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              {update.isPending ? "Salvando…" : "Salvar"}
              <Save size={13} strokeWidth={2} color="#fff" />
            </button>
          </div>

          {update.isError && (
            <div
              style={{
                padding: 10,
                background: "#fff0ee",
                border: "1px solid #ff5577",
                borderRadius: 8,
                color: "#cc3344",
                fontSize: 12,
              }}
            >
              Erro ao salvar: {(update.error as Error)?.message ?? "tenta de novo"}
            </div>
          )}

          {/* Delete confirm modal */}
          {showDeleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(5,8,16,0.6)",
                backdropFilter: "blur(4px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
                zIndex: 100,
              }}
              onClick={() => setShowDeleteConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 8 }}
                animate={{ scale: 1, y: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="wf-card"
                style={{
                  padding: 22,
                  background: "var(--wf-paper)",
                  maxWidth: 420,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: "rgba(255,87,119,0.12)",
                      color: "#ff5577",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Trash2 size={17} strokeWidth={1.75} />
                  </div>
                  <h3
                    className="wf-display"
                    style={{ fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: "-0.01em" }}
                  >
                    Deletar essa base?
                  </h3>
                </div>
                <p style={{ fontSize: 13, color: "var(--wf-ink-2)", lineHeight: 1.5 }}>
                  <strong>{kb.name}</strong> vai sumir permanentemente. Os agentes não vão mais
                  ter esse contexto nas próximas tarefas.
                </p>
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="wf-btn wf-btn--ghost wf-btn--sm"
                  >
                    Cancelar
                  </button>
                  <div style={{ flex: 1 }} />
                  <button
                    type="button"
                    onClick={() => del.mutate()}
                    disabled={del.isPending}
                    className="wf-btn"
                    style={{
                      background: "#ff5577",
                      color: "#fff",
                      borderColor: "#cc3344",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {del.isPending ? "Deletando…" : "Sim, deletar"}
                    <Trash2 size={13} strokeWidth={2} color="#fff" />
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
